import { useEffect, useState } from 'react';

/**
 * Restores trackpad/wheel scrolling for a scrollable element rendered inside a
 * Radix Dialog (or any `react-remove-scroll` lock) **when the app is mounted in
 * a shadow root** — which is how every federation remote runs
 * (`federation-mount.ts` calls `attachShadow`).
 *
 * Why it breaks: `react-remove-scroll` registers its `wheel` listener on
 * `document`. Events that originate inside a shadow tree are *retargeted* by
 * the time a document-level listener sees them — `event.target` becomes the
 * shadow **host**, not the inner scroll container. The lock therefore checks
 * the host, finds nothing scrollable, concludes the gesture would scroll the
 * locked background, and calls `preventDefault()`. Dragging the scrollbar still
 * works because that is not a wheel event, which is the tell-tale symptom.
 *
 * The fix: handle the wheel on the container itself and `stopPropagation()`, so
 * the event never reaches the document listener that would cancel it. We apply
 * the delta ourselves because `preventDefault()` upstream would otherwise have
 * killed the native scroll too.
 *
 * The listener is registered non-passively (`passive: false`) — a passive
 * listener may not call `preventDefault()`, and React's `onWheel` prop is
 * passive by default, so this has to be a manual `addEventListener`.
 *
 * Usage:
 *   const scrollRef = useShadowDomWheelScroll<HTMLDivElement>();
 *   <div ref={scrollRef} className="overflow-y-auto max-h-80">…</div>
 */
export function useShadowDomWheelScroll<T extends HTMLElement>() {
  // State, not a plain ref: a conditionally-rendered container (e.g. the
  // Add Criterion modal's form view, which unmounts under the progress view
  // and can remount later) calls the ref callback again with a new node, but
  // an effect with `[]` deps never re-runs to pick it up. Storing the node in
  // state makes it a real dependency, so the effect below reattaches on every
  // mount of a new node.
  const [element, setElement] = useState<T | null>(null);

  useEffect(() => {
    if (!element) return;

    // Only intervene when we are actually inside a shadow tree; in the
    // standalone (light-DOM) build the native behaviour is correct and
    // hijacking the wheel would only make scrolling feel less native.
    if (!element.getRootNode || !(element.getRootNode() instanceof ShadowRoot)) return;

    const onWheel = (event: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;

      // At a boundary and still scrolling outward: let the event bubble so the
      // lock can suppress it as intended, rather than trapping the gesture.
      const atTop = scrollTop <= 0 && event.deltaY < 0;
      const atBottom = scrollTop >= maxScroll && event.deltaY > 0;
      if (atTop || atBottom) return;

      // Own the gesture: apply it, and keep it away from the document-level
      // listener that would otherwise preventDefault it.
      event.preventDefault();
      event.stopPropagation();
      element.scrollTop = Math.min(Math.max(scrollTop + event.deltaY, 0), maxScroll);
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [element]);

  return setElement;
}
